import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import {
  useWorkspaces,
  useRepositories,
  usePipelineRuns,
  usePipelines,
  useToast,
} from "../hooks";
import {
  WorkspaceSelector,
  DeploymentTrigger,
  GroupedPipelineRunsTable,
} from "../components/pipeline";

const PipelineRunsPage = () => {
  const { workspace, repoSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Use custom hooks
  const {
    workspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    isLoading,
    error: workspaceError,
    fetchWorkspaces,
  } = useWorkspaces(workspace);

  const {
    repositories,
    selectedRepository,
    setSelectedRepository,
    viewAllRepos,
    setViewAllRepos,
    setIsLoadingAllRepos,
    fetchRepositories,
    handleRepositoryChange,
  } = useRepositories(repoSlug);

  const {
    pipelineRuns,
    isLoadingRuns,
    error: pipelineRunsError,
    fetchPipelineRuns,
    fetchPipelinesForAllRepos,
    clearPipelineRuns,
  } = usePipelineRuns();

  const {
    selectedPipelineConfig,
    setSelectedPipelineConfig,
    isTriggeringPipeline,
    fetchPipelines,
    triggerPipeline,
    stopPipeline,
  } = usePipelines();

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRunIds, setExpandedRunIds] = useState([]);

  // Load workspaces on initial render
  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkspaces();
    }
  }, [isAuthenticated, fetchWorkspaces]);

  // Load repositories when workspace changes
  useEffect(() => {
    if (selectedWorkspace) {
      fetchRepositories(selectedWorkspace);
    }
  }, [selectedWorkspace, fetchRepositories]);

  // Load pipeline runs when repository changes
  useEffect(() => {
    if (selectedWorkspace) {
      if (viewAllRepos) {
        setIsLoadingAllRepos(true);
        fetchPipelinesForAllRepos(selectedWorkspace, repositories).finally(
          () => {
            setIsLoadingAllRepos(false);
          }
        );

        // Update URL if needed
        if (workspace !== selectedWorkspace) {
          navigate(`/pipelines-status/${selectedWorkspace}`);
        }
      } else if (selectedRepository) {
        fetchPipelineRuns(selectedWorkspace, selectedRepository, repositories);
        fetchPipelines(selectedWorkspace, selectedRepository);

        // Update URL if needed
        if (
          !workspace ||
          !repoSlug ||
          workspace !== selectedWorkspace ||
          repoSlug !== selectedRepository
        ) {
          navigate(
            `/pipelines-status/${selectedWorkspace}/${selectedRepository}`
          );
        }
      }
    }
  }, [
    selectedWorkspace,
    selectedRepository,
    navigate,
    workspace,
    repoSlug,
    viewAllRepos,
    repositories,
    fetchPipelineRuns,
    fetchPipelines,
    fetchPipelinesForAllRepos,
    setIsLoadingAllRepos,
  ]);

  const handleWorkspaceChange = (workspace) => {
    setSelectedWorkspace(workspace);
    if (!viewAllRepos) {
      setSelectedRepository("");
    }
    clearPipelineRuns();
  };

  const handleRefreshPipelines = () => {
    if (selectedWorkspace) {
      if (viewAllRepos) {
        fetchPipelinesForAllRepos(selectedWorkspace, repositories);
      } else if (selectedRepository) {
        fetchPipelineRuns(selectedWorkspace, selectedRepository, repositories);
      }
    }
  };

  const handleTriggerPipeline = async (repoSlug, branch, pattern) => {
    const success = await triggerPipeline(
      selectedWorkspace,
      repoSlug,
      branch,
      pattern
    );
    if (success) {
      // Refresh the pipeline runs after a short delay
      setTimeout(() => {
        if (viewAllRepos) {
          fetchPipelinesForAllRepos(selectedWorkspace, repositories);
        } else {
          fetchPipelineRuns(
            selectedWorkspace,
            selectedRepository,
            repositories
          );
        }
      }, 1000);
    }
  };

  const handleStopPipeline = async (pipelineUuid, repoSlug) => {
    // When in viewAllRepos mode, we need to use the repository slug from the pipeline run
    const repositorySlug = viewAllRepos ? repoSlug : selectedRepository;

    const success = await stopPipeline(
      selectedWorkspace,
      repositorySlug,
      pipelineUuid
    );
    if (success) {
      // Refresh the pipeline runs after a short delay
      setTimeout(() => {
        if (viewAllRepos) {
          fetchPipelinesForAllRepos(selectedWorkspace, repositories);
        } else {
          fetchPipelineRuns(
            selectedWorkspace,
            selectedRepository,
            repositories
          );
        }
      }, 1000);
    }
  };

  const toggleExpandRun = (uuid) => {
    setExpandedRunIds((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Filter pipeline runs based on search term
  const filteredPipelineRuns = pipelineRuns.filter((run) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const buildNumberStr = String(run.build_number);
      const refName = run.target?.ref_name?.toLowerCase() || "";
      const selectorPattern =
        run.target?.selector?.pattern?.toLowerCase() || "";

      return (
        buildNumberStr.includes(searchLower) ||
        refName.includes(searchLower) ||
        selectorPattern.includes(searchLower)
      );
    }
    return true;
  });

  // Handle errors
  const displayError = workspaceError || pipelineRunsError;
  if (displayError) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="text-destructive text-lg font-semibold">
          {displayError}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0">
      <Card className="mb-6">
        <WorkspaceSelector
          workspaces={workspaces}
          selectedWorkspace={selectedWorkspace}
          onWorkspaceChange={handleWorkspaceChange}
          repositories={repositories}
          selectedRepository={selectedRepository}
          viewAllRepos={viewAllRepos}
          onRepositoryChange={handleRepositoryChange}
        />
      </Card>

      {selectedWorkspace && (viewAllRepos || selectedRepository) && (
        <>
          {!viewAllRepos && selectedRepository && (
            <Card className="mb-6">
              <DeploymentTrigger
                selectedPipelineConfig={selectedPipelineConfig}
                setSelectedPipelineConfig={setSelectedPipelineConfig}
                isTriggeringPipeline={isTriggeringPipeline}
                onTriggerPipeline={handleTriggerPipeline}
                toast={toast}
                repositories={repositories}
                selectedRepository={selectedRepository}
                workspace={selectedWorkspace}
              />
            </Card>
          )}

          <Card>
            <GroupedPipelineRunsTable
              viewAllRepos={viewAllRepos}
              title={
                viewAllRepos
                  ? "Running & Failed Pipelines (All Repositories)"
                  : "Running & Failed Pipelines"
              }
              pipelineRuns={filteredPipelineRuns}
              isLoadingRuns={isLoadingRuns}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleRefreshPipelines={handleRefreshPipelines}
              expandedRunIds={expandedRunIds}
              toggleExpandRun={toggleExpandRun}
              handleStopPipeline={handleStopPipeline}
              setViewAllRepos={setViewAllRepos}
              setSelectedRepository={setSelectedRepository}
              formatDateTime={formatDateTime}
              workspace={selectedWorkspace}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default PipelineRunsPage;
